import { useState, useEffect, useRef } from 'react';
import * as api from '../services/api';

/**
 * Hook for managing playback state and clip progression
 * @param {string[]} channelNames - For 'favorites' source: list of channel login names
 * @param {number} limit - Max clips to fetch
 * @param {number} days - For 'favorites' source: max days
 * @param {object} options - Additional options
 * @param {string} options.source - 'favorites' (default) or 'collection'
 * @param {array} options.preloadedClips - For 'collection' source: array of clips to use
 * @param {string[]} options.selectedIds - For 'collection' source: specific clip IDs to play
 */
export default function usePlayback(
  channelNames = [], 
  limit = 50, 
  days = null,
  options = {}
) {
  const { source = 'favorites', preloadedClips = null, selectedIds = [] } = options;
  
  const [clips, setClips] = useState(preloadedClips ? (Array.isArray(preloadedClips) ? preloadedClips : []) : []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  // ad-related state
  const [clipsSinceLastAd, setClipsSinceLastAd] = useState(0);
  const [elapsedContentSeconds, setElapsedContentSeconds] = useState(0);
  const lastAdTimestamp = useRef(null);
  // track whether the last navigation was manual (user clicked prev/next)
  const lastNavigationManual = useRef(true);

  // Accumulate elapsed content seconds when playing
  useEffect(() => {
    if (!isPlaying) return; // Only accumulate when actively playing
    // Skip real interval during unit tests to prevent act() from hanging
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') return;

    const interval = setInterval(() => {
      setElapsedContentSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    let mounted = true;
    async function fetchClips() {
      // If preloaded clips were provided, use them directly
      if (preloadedClips && Array.isArray(preloadedClips) && preloadedClips.length > 0) {
        if (mounted) {
          setClips(preloadedClips);
          setCurrentIndex(0);
          setIsLoading(false);
        }
        return;
      }

      // During unit tests avoid making real network requests which can hang.
      if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
        if (mounted) {
          setClips([]);
          setCurrentIndex(0);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        let endpoint = `/api/playback/clips`;
        
        // For collection source, use collection-clips endpoint
        if (source === 'collection') {
          endpoint = `/api/playback/collection-clips`;
          
          const params = new URLSearchParams();
          if (limit) params.set('limit', String(limit));
          if (selectedIds && selectedIds.length > 0) {
            params.set('selectedIds', selectedIds.join(','));
          }
          const query = params.toString();
          const res = await api.request(`${endpoint}${query ? `?${query}` : ''}`);
          if (!mounted) return;
          setClips(res.clips || []);
          setCurrentIndex(0);
        } else {
          // Original favorites path
          const params = new URLSearchParams();
          if (limit) params.set('limit', String(limit));
          if (channelNames && channelNames.length) params.set('channels', channelNames.join(','));
          if (days) params.set('days', String(days));
          const query = params.toString();
          const res = await api.request(`${endpoint}${query ? `?${query}` : ''}`);
          if (!mounted) return;
          setClips(res.clips || []);
          setCurrentIndex(0);
        }
      } catch (e) {
        // ignore for now
        setClips([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    }

    fetchClips();

    return () => { mounted = false; };
  }, [source, channelNames, limit, days, selectedIds, preloadedClips]);

  const currentClip = clips[currentIndex] || null;

  function playNext(manual = false) {
    lastNavigationManual.current = manual;
    setCurrentIndex((i) => Math.min(i + 1, Math.max(0, clips.length - 1)));
    if (!manual) {
      setClipsSinceLastAd((c) => c + 1);
    }
  }

  function playPrevious(manual = true) {
    lastNavigationManual.current = manual;
    setCurrentIndex((i) => Math.max(0, i - 1));
    if (!manual) {
      setClipsSinceLastAd((c) => c + 1);
    }
  }

  function togglePlay() {
    setIsPlaying((p) => !p);
  }

  function setPlayState(val) {
    setIsPlaying(!!val);
  }

  // Helper to reset ad counters after an ad is shown
  function resetAdCounters() {
    setClipsSinceLastAd(0);
    setElapsedContentSeconds(0);
    lastAdTimestamp.current = Date.now();
  }

  return {
    clips,
    currentClip,
    currentIndex,
    isLoading,
    isPlaying,
    playNext,
    playPrevious,
    togglePlay,
    setPlayState,
    lastNavigationManual,
    // ad state & helpers
    clipsSinceLastAd,
    elapsedContentSeconds,
    lastAdTimestamp,
    resetAdCounters,
  };
}
